import { useStore } from '@tanstack/react-form';
import { useMutation } from '@tanstack/react-query';
import { useAtomValue, useSetAtom, useStore as useJotaiStore } from 'jotai';
import { HTTPError } from 'ky';
import { omit } from 'lodash-es';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import type { DataTableSelectionState } from 'components/data-table/types';
import { api } from 'lib/request/api';

import {
  assetNameCheckAtom,
  createPackageDraftAtom,
  editPackageDetailAtom,
  verificationStatesAtom,
  type SourceSelectAllAtom,
} from './atoms';
import {
  EMPTY_ASSET_LIST,
  EMPTY_ASSET_NAME_CHECK,
  assetNameBatches,
  assetNamesToCheck,
  mergeAssetNameCheck,
  summarizeAssetList,
  workingAssetNames,
  type AssetListSummary,
} from './asset-list-utils';
import {
  isHeaderClick,
  selectAllCovers,
  sourceSelectionState,
  visibleSelectionDiff,
  type SelectAllSnapshot,
} from './source-selection';
import type {
  AssetListCheckResponse,
  AssetListUploadResponse,
  FlatSlotRow,
  InterestBreakdown,
  PackageFormInstance,
  PackageFormValues,
  SourceRow,
  SourceSelectAllParams,
  SourceSelectAllRow,
  SourceSelectionChange,
  VerificationRollup,
  VerificationType,
} from './types';
import {
  DEFAULT_VERIFICATION_STATE,
  INITIAL_FORM,
  buildHydratedStates,
  getSubtractVerification,
  packageDetailToFormValues,
  subscribeForDraftMaterialization,
  verificationKey,
  type DraftMaterializationWatcher,
} from './utils';

/**
 * Slot table hydrate hook. Functional setter merges new BE values onto
 * the previous map; `buildHydratedStates` short-circuits to the same
 * reference when nothing differs, so a stable refetch is a no-op.
 *
 * @param {FlatSlotRow[]} rows - Rows from the slot fetch (memoize upstream)
 * @return {void}
 */
export const useHydrateVerifications = (rows: FlatSlotRow[]): void => {
  const setStates = useSetAtom(verificationStatesAtom);
  useEffect(() => {
    setStates((prev) => buildHydratedStates(prev, rows));
  }, [rows, setStates]);
};

/** Return shape of {@link useNriVerification}. */
export interface NriVerificationCellProps {
  status: boolean | null;
  commentsCount: number;
  onStatusChange: (next: boolean | null) => void;
  onCommentsCountChange: (next: number) => void;
}

/**
 * Cell-level hook. Returns the four props `<VerificationControls>` wants:
 * `status`, `commentsCount`, and stable partial-update callbacks that
 * round-trip changes back into the atom map. The same atom backs both
 * cells and the rollup, so cell edits auto-propagate.
 *
 * @param {number} nriCalculationId - Calculation id from the BE shape
 * @param {VerificationType} type - 'nri' or 'wi'
 * @return {NriVerificationCellProps} Status + count + change callbacks
 */
export const useNriVerification = (
  nriCalculationId: number,
  type: VerificationType
): NriVerificationCellProps => {
  const key = verificationKey(nriCalculationId, type);
  const states = useAtomValue(verificationStatesAtom);
  const setStates = useSetAtom(verificationStatesAtom);
  const state = states[key] ?? DEFAULT_VERIFICATION_STATE;

  const onStatusChange = useCallback(
    (next: boolean | null) =>
      setStates((prev) => ({
        ...prev,
        [key]: {
          ...DEFAULT_VERIFICATION_STATE,
          ...prev[key],
          status: next,
        },
      })),
    [key, setStates]
  );

  const onCommentsCountChange = useCallback(
    (next: number) =>
      setStates((prev) => ({
        ...prev,
        [key]: {
          ...DEFAULT_VERIFICATION_STATE,
          ...prev[key],
          commentsCount: next,
        },
      })),
    [key, setStates]
  );

  return {
    status: state.status,
    commentsCount: state.commentsCount,
    onStatusChange,
    onCommentsCountChange,
  };
};

/**
 * Parent-row rollup hook. Walks the row's `subtracts`, reads each
 * per-key state from the atom map, and falls back to the subtract's BE
 * verification status for entries the user hasn't touched. Returns
 * `<RollupVerificationStatus>`-shaped counts.
 *
 * @param {InterestBreakdown[] | null} children - Row's subtract list
 * @param {VerificationType} type - 'nri' or 'wi'
 * @return {VerificationRollup} Aggregated counts
 */
export const useNriRollup = (
  children: InterestBreakdown[] | null,
  type: VerificationType
): VerificationRollup => {
  const states = useAtomValue(verificationStatesAtom);
  return useMemo(() => {
    if (!children || children.length === 0) {
      return {
        totalSteps: 0,
        markedCorrectTotal: 0,
        markedIncorrectTotal: 0,
      };
    }
    let totalSteps = 0;
    let markedCorrectTotal = 0;
    let markedIncorrectTotal = 0;
    for (const c of children) {
      if (c.nriCalculationId === null) continue;
      const cellState = states[verificationKey(c.nriCalculationId, type)];
      const status = cellState
        ? cellState.status
        : getSubtractVerification(c, type).status;
      totalSteps++;
      if (status === true) markedCorrectTotal++;
      else if (status === false) markedIncorrectTotal++;
    }
    return { totalSteps, markedCorrectTotal, markedIncorrectTotal };
  }, [children, states, type]);
};

/** Return shape of {@link useSourceSelectAll}. */
export interface SourceSelectAllState {
  /** True while the bulk fetch is in flight — freeze selection interaction. */
  isFetching: boolean;
  /** Message from the last failed bulk fetch, or null. */
  error: string | null;
  /**
   * The last select-all that went through, or null once a deselect-all has
   * run. Feed it to `selectAllCovers` to tell whether the header may read
   * checked.
   */
  lastSelectAll: SelectAllSnapshot | null;
  /**
   * Fetch every row matching the table's current scope and report it as a
   * selection delta: `select` true unions the returned rows in, false drops
   * exactly those ids (so selections outside the current filter survive a
   * deselect-all).
   */
  run: (select: boolean) => Promise<void>;
}

// Drops anything past the tuple rather than passing the row through: an
// entity source has no sellers or acquirers to carry.
const toSourceTuple = ({ id, name, href }: SourceSelectAllRow): SourceRow => ({
  id,
  name,
  href,
});

/** The scope a source table's select-all runs under. */
export interface SourceSelectAllScope {
  /** The table's live search / filter / sort, minus paging. */
  params: SourceSelectAllParams;
  /**
   * Whether the table's tile is the one picked. Both source tables stay
   * mounted across tile flips, so a flip is a scope change the params alone
   * don't show.
   */
  active: boolean;
}

/**
 * Drives one source table's "select all" header checkbox. The header operates
 * on the whole filtered match set, not the visible page, so it can't be
 * resolved from table data — it goes to the matching `select_all` endpoint
 * with the table's live search / filter / sort scope.
 *
 * A response only applies while it is the latest request and the scope it ran
 * under still holds. A newer run, a search / filter / sort change, a tile flip
 * or an unmount each make it stale, and a stale response is dropped — it
 * describes rows the user is no longer looking at.
 *
 * On failure the selection is left exactly as it was and the message is
 * returned for the table to surface; a partial application would be worse
 * than none, since the user can't see which rows were affected.
 *
 * Loading and error are local state rather than a `mutationAtomWithLoadable`
 * loadable: that loadable is global to the atom, so a failure would outlive
 * the table that hit it and greet the next mount with a stale banner.
 *
 * @param {SourceSelectAllAtom} selectAllAtom - Resource-specific bulk-fetch atom
 * @param {SourceSelectAllScope} scope - The table's live scope
 * @param {Function} onChange - Receives the resolved selection delta
 * @param {Function} mapRow - Turns a returned row into the stored source row;
 *   the packages table passes its own so bulk-picked rows carry the same
 *   sellers and acquirers a row picked from the table does
 * @return {SourceSelectAllState} Fetch state + the dispatcher
 */
export const useSourceSelectAll = <TRow extends SourceSelectAllRow>(
  selectAllAtom: SourceSelectAllAtom<TRow>,
  { params, active }: SourceSelectAllScope,
  onChange: (change: SourceSelectionChange) => void,
  mapRow: (row: TRow) => SourceRow = toSourceTuple
): SourceSelectAllState => {
  const fetchSelection = useSetAtom(selectAllAtom);
  const [isFetching, setIsFetching] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [lastSelectAll, setLastSelectAll] = useState<SelectAllSnapshot | null>(
    null
  );
  // Id of the request whose response may still apply. Bumped by every run and
  // by every scope change, so any response carrying an older id is stale.
  const requestId = useRef(0);

  // Compared by value: the params object is rebuilt on every page change,
  // which doesn't change what a select-all covers.
  const scopeKey = JSON.stringify(params);
  useEffect(
    () => () => {
      requestId.current += 1;
      setIsFetching(false);
    },
    [scopeKey, active]
  );

  const run = useCallback(
    async (select: boolean) => {
      requestId.current += 1;
      const id = requestId.current;
      const isCurrent = () => id === requestId.current;
      // Clear on entry, not only on success: a stale failure banner must not
      // outlive the retry that fixed it.
      setError(null);
      setIsFetching(true);
      try {
        const { results } = await fetchSelection(params);
        if (!isCurrent()) return;
        const ids = results.map((result) => result.id);
        setLastSelectAll(select ? { params, ids } : null);
        onChange(
          select
            ? { added: results.map(mapRow), removed: [] }
            : { added: [], removed: ids }
        );
      } catch (err) {
        if (!isCurrent()) return;
        setError(
          err instanceof Error ? err.message : 'Failed to update the selection'
        );
      } finally {
        if (isCurrent()) setIsFetching(false);
      }
    },
    [fetchSelection, params, onChange, mapRow]
  );

  return { isFetching, error, lastSelectAll, run };
};

/** What a Step 0 source table hands {@link useSourceTableSelection}. */
export interface SourceTableSelectionInput<TRow extends SourceSelectAllRow> {
  selectAllAtom: SourceSelectAllAtom<TRow>;
  /** The table's live search payload; select-all runs it without paging. */
  payload: object | undefined;
  /** The visible page. */
  rows: TRow[];
  totalCount: number;
  /** Every selected id, across pages. The form owns this. */
  selectedIds: Set<number>;
  /** Whether the table's tile is the one picked. */
  active: boolean;
  onSelectionChange: (change: SourceSelectionChange) => void;
  /** Turns a picked row, visible or bulk-fetched, into the stored source row. */
  mapRow?: (row: TRow) => SourceRow;
}

/** The table's controlled selection, its change handler, and the select-all fetch state. */
export interface SourceTableSelection {
  controlledSelection: DataTableSelectionState;
  handleSelectionChange: (next: DataTableSelectionState) => void;
  isSelectingAll: boolean;
  selectAllError: string | null;
}

/**
 * The multi-select wiring both Step 0 source tables share. The form's
 * selection drives the table; a header click runs select-all over every
 * match, and a row click reports the visible page's delta.
 *
 * @param {SourceTableSelectionInput} input - The table's page, scope and selection
 * @return {SourceTableSelection} Controlled selection + change handler
 */
export const useSourceTableSelection = <TRow extends SourceSelectAllRow>({
  selectAllAtom,
  payload,
  rows,
  totalCount,
  selectedIds,
  active,
  onSelectionChange,
  mapRow = toSourceTuple,
}: SourceTableSelectionInput<TRow>): SourceTableSelection => {
  const visibleIds = useMemo(() => rows.map((row) => row.id), [rows]);
  // Same scope the table is displaying, minus paging — the bulk endpoint is
  // unpaginated, and select-all must cover every match, not one page of them.
  const selectAllParams = useMemo<SourceSelectAllParams>(
    () => omit(payload ?? {}, ['page', 'pageSize']),
    [payload]
  );
  const { isFetching, error, lastSelectAll, run } = useSourceSelectAll(
    selectAllAtom,
    { params: selectAllParams, active },
    onSelectionChange,
    mapRow
  );

  const controlledSelection = useMemo<DataTableSelectionState>(
    () =>
      sourceSelectionState(
        visibleIds,
        selectedIds,
        totalCount,
        selectAllCovers(lastSelectAll, selectAllParams, selectedIds)
      ),
    [visibleIds, selectedIds, totalCount, lastSelectAll, selectAllParams]
  );

  const handleSelectionChange = useCallback(
    (next: DataTableSelectionState) => {
      if (isHeaderClick(next, controlledSelection)) {
        run(next.selectAll);
        return;
      }

      const { addedIds, removedIds } = visibleSelectionDiff(
        next,
        visibleIds,
        selectedIds
      );
      if (addedIds.length === 0 && removedIds.length === 0) return;

      const added = addedIds.flatMap((id) => {
        const row = rows.find((candidate) => candidate.id === id);
        return row ? [mapRow(row)] : [];
      });
      onSelectionChange({ added, removed: removedIds });
    },
    [
      controlledSelection,
      run,
      visibleIds,
      selectedIds,
      rows,
      mapRow,
      onSelectionChange,
    ]
  );

  return {
    controlledSelection,
    handleSelectionChange,
    isSelectingAll: isFetching,
    selectAllError: error,
  };
};

/**
 * Edit-mode bootstrap state machine. `'unsupported'` and `'error'` both
 * trigger a page-level redirect to `/packages` — `'unsupported'` for
 * legacy (`type !== ''`) packages, `'error'` for any failure in the
 * GET → POST → Pusher chain.
 */
export type EditModeBootstrapState =
  | { state: 'loading' }
  | { state: 'error'; error: string }
  | { state: 'unsupported' }
  | { state: 'ready'; defaults: PackageFormValues };

/**
 * Page-level bootstrap orchestrator. For `packageId === null` (create
 * mode) returns `INITIAL_FORM` synchronously. For edit mode, runs:
 *
 *   1. `GET /packages/:id`
 *   2. Type guard — bail with `'unsupported'` if `pkg.type !== ''`
 *   3. `POST /packages/drafts` with `{ asOfDate, sourcePackageId }` so
 *      the BE seeds slots from the package's existing assets and
 *      preserves its exclusion state.
 *   4. Pusher subscribe + await `status_change: 'ready'`, with the
 *      watcher's status-check fallback armed (draft id passed) so a
 *      missed event is recovered from the persisted draft status; the
 *      effect cleanup cancels the watcher on unmount.
 *   5. Resolve to `'ready'` with assembled form defaults.
 *
 * Any failure routes to `'error'` with a `retry` that re-runs the chain
 * from step 1. The page-level gate renders a single skeleton until this
 * resolves to `'ready'`, so the wizard form mounts only after the draft
 * has materialized — Step 1 navigation never sees a null `draftId`.
 *
 * @param {string | null} packageId - URL `:id` for edit, null for create
 * @return {EditModeBootstrapState} Current bootstrap status
 */
export const useEditModeBootstrap = (
  packageId: string | null
): EditModeBootstrapState => {
  const fetchPackage = useSetAtom(editPackageDetailAtom);
  const createDraft = useSetAtom(createPackageDraftAtom);
  const [state, setState] = useState<EditModeBootstrapState>(() => {
    if (packageId === null) return { state: 'ready', defaults: INITIAL_FORM };
    return { state: 'loading' };
  });

  useEffect(() => {
    if (packageId === null) {
      setState({ state: 'ready', defaults: INITIAL_FORM });
      return undefined;
    }

    let cancelled = false;
    // Held so the cleanup can tear the watcher down mid-wait — otherwise
    // an unmount during materialization would leave the status-check
    // fallback polling until the timeout ceiling.
    let activeWatcher: DraftMaterializationWatcher | null = null;
    setState({ state: 'loading' });

    (async () => {
      try {
        const pkg = await fetchPackage(packageId);
        if (cancelled) return;
        if (pkg.type !== '') {
          setState({ state: 'unsupported' });
          return;
        }
        if (pkg.asOfDate === null) {
          throw new Error('Package is missing as_of_date');
        }
        const draft = await createDraft({
          asOfDate: pkg.asOfDate,
          sourcePackageId: pkg.id,
        });
        if (cancelled) return;
        const watcher = subscribeForDraftMaterialization(draft.pusherChannel, {
          draftId: draft.id,
        });
        activeWatcher = watcher;
        try {
          await watcher.wait();
        } finally {
          watcher.cancel();
          activeWatcher = null;
        }
        if (cancelled) return;
        setState({
          state: 'ready',
          defaults: packageDetailToFormValues(
            pkg,
            draft.id,
            draft.typeCurveScenarioId
          ),
        });
      } catch (err) {
        if (cancelled) return;
        setState({
          state: 'error',
          error: err instanceof Error ? err.message : 'Failed to load package',
        });
      }
    })();

    return () => {
      cancelled = true;
      activeWatcher?.cancel();
    };
  }, [packageId, fetchPackage, createDraft]);

  return state;
};

/** The name check endpoint takes at most this many names per request. */
export const ASSET_NAME_CHECK_BATCH = 10_000;
/** How long the list must sit still before its new names are checked. */
export const ASSET_NAME_CHECK_DEBOUNCE_MS = 400;

/**
 * The asset list read against the name check: the working list, which names
 * matched, which did not, which are still unanswered, the contributed Tracts
 * and their owning entities.
 *
 * @param {PackageFormInstance} form - Wizard form instance
 * @return {AssetListSummary} The list's results
 */
export const useAssetListSummary = (
  form: PackageFormInstance
): AssetListSummary => {
  const input = useStore(form.store, (s) => s.values.assetList);
  const check = useAtomValue(assetNameCheckAtom);
  const names = useMemo(() => workingAssetNames(input), [input]);
  return useMemo(() => summarizeAssetList(names, check), [names, check]);
};

/**
 * The server's reason for refusing a check (`{ errors: { assetNames } }`),
 * or null for any other failure.
 *
 * @param {unknown} error - What the request threw
 * @return {Promise<string | null>} The reason
 */
const checkFailureReason = async (error: unknown): Promise<string | null> => {
  if (!(error instanceof HTTPError)) return null;
  const body: unknown = await error.response.json().catch(() => null);
  const reasons =
    body !== null && typeof body === 'object' && 'errors' in body
      ? (body.errors as { assetNames?: unknown } | null)?.assetNames
      : undefined;
  return Array.isArray(reasons) && typeof reasons[0] === 'string'
    ? reasons[0]
    : null;
};

/** One run of the check: what abandons it. */
interface NameCheckRun {
  signal: AbortSignal;
}

/**
 * The most passes one run of the check makes. The first sends the names with no
 * answer yet; one of them can join a Tract an earlier answer showed, so the
 * next sends that Tract's names together. A list that still needs more after
 * this fails the check, offering Try again.
 */
const ASSET_NAME_CHECK_PASSES = 3;

/**
 * Checks the asset list's names. `schedule` — called by whatever changes the
 * list, and on any change to the list made elsewhere (a header tag or the file
 * taken out) — waits {@link ASSET_NAME_CHECK_DEBOUNCE_MS}, then sends the names
 * of the working list with no answer yet, and every name of a Tract the server
 * has to work out again ({@link assetNamesToCheck}: whether a Tract is whole or
 * held in part depends on all its names, so taking one out can change it too),
 * {@link ASSET_NAME_CHECK_BATCH} at most per request, one request after another,
 * merging each answer as it lands, and passes again until no Tract is left to
 * work out. Each `schedule` abandons the run in flight, as the bulk upload's dry
 * run does. One failed request fails the check; scheduling again re-sends the
 * names still unanswered. `cancel` abandons everything, for a tile flip.
 *
 * On mount it schedules once, so a list left part-checked (by moving off the
 * step mid-check) resumes.
 *
 * @param {PackageFormInstance} form - Wizard form instance
 * @return {Object} `schedule(delayMs?)` and `cancel()`
 */
export const useAssetNameCheck = (form: PackageFormInstance) => {
  const store = useJotaiStore();
  const setCheck = useSetAtom(assetNameCheckAtom);
  const input = useStore(form.store, (s) => s.values.assetList);
  const timer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  const inFlight = useRef<AbortController | null>(null);

  const namesToCheck = useCallback(
    () =>
      assetNamesToCheck(
        workingAssetNames(form.state.values.assetList),
        store.get(assetNameCheckAtom)
      ),
    [form, store]
  );

  const { mutate } = useMutation<void, Error, NameCheckRun>({
    mutationFn: async ({ signal }) => {
      for (let pass = 0; pass < ASSET_NAME_CHECK_PASSES; pass += 1) {
        const groups = namesToCheck();
        if (groups.length === 0) return;
        for (const batch of assetNameBatches(groups, ASSET_NAME_CHECK_BATCH)) {
          const response = await api
            .post('validations/packages/asset_lists', {
              json: { assetNames: batch },
              signal,
            })
            .json<AssetListCheckResponse>();
          setCheck((check) => mergeAssetNameCheck(check, batch, response));
        }
      }
      if (namesToCheck().length > 0) {
        throw new Error('The asset list could not be grouped.');
      }
    },
    retry: false,
    // Offline, fail now with "Names couldn't be validated." rather than wait, paused, on
    // "Validating…" for a connection that may not come back.
    networkMode: 'always',
  });

  const cancel = useCallback(() => {
    clearTimeout(timer.current);
    inFlight.current?.abort();
    inFlight.current = null;
  }, []);

  const run = useCallback(() => {
    if (namesToCheck().length === 0) return;
    const controller = new AbortController();
    inFlight.current = controller;
    mutate(
      { signal: controller.signal },
      {
        onError: (error) => {
          void checkFailureReason(error).then((reason) => {
            // Abandoned (by a change or a tile flip), not failed.
            if (controller.signal.aborted) return;
            setCheck((check) => ({ ...check, failure: { reason } }));
          });
        },
      }
    );
  }, [namesToCheck, mutate, setCheck]);

  const schedule = useCallback(
    (delayMs = ASSET_NAME_CHECK_DEBOUNCE_MS) => {
      cancel();
      // A new attempt: the last failure no longer describes the list.
      setCheck((check) =>
        check.failure === null ? check : { ...check, failure: null }
      );
      timer.current = setTimeout(run, delayMs);
    },
    [cancel, run, setCheck]
  );

  // `input` too: a header tag or the file taken out changes the list without going through the
  // panel, and taking a name out can change how its Tract is worked out.
  useEffect(() => {
    schedule();
    return cancel;
  }, [schedule, cancel, input]);

  return { schedule, cancel };
};

/** The file types the upload endpoint reads (not .xls, which it refuses). */
export const ASSET_LIST_EXTENSIONS = ['.csv', '.txt', '.tsv', '.xlsx'];
/** The upload endpoint's limit, checked here too so a big file never posts. */
export const MAX_ASSET_LIST_FILE_BYTES = 10 * 1024 * 1024;

/**
 * The browser-side file checks, worded as the server words them; the server
 * repeats them.
 *
 * @param {File} file - The chosen file
 * @return {string | null} Why the file cannot be uploaded, or null
 */
export const checkAssetListFile = (file: File): string | null => {
  const dot = file.name.lastIndexOf('.');
  const extension = dot < 0 ? '' : file.name.slice(dot).toLowerCase();
  if (!ASSET_LIST_EXTENSIONS.includes(extension)) {
    return `"${file.name}" is not a supported file type. Upload a .csv, .tsv, .txt, or .xlsx file.`;
  }
  if (file.size > MAX_ASSET_LIST_FILE_BYTES) {
    return `"${file.name}" is larger than 10 MB. Remove the columns other than asset names and upload again.`;
  }
  return null;
};

/**
 * Reads one file of Asset Names on the server, one file at a time: a file that
 * yields names replaces the listed file and its names (and `onNames` runs);
 * one that fails, in the browser or on the server, shows why and leaves the
 * listed file as it was. Only the latest upload's answer is applied, so a
 * second file overtakes one still in flight; `reset` drops the one in flight,
 * for a tile flip.
 *
 * @param {PackageFormInstance} form - Wizard form instance
 * @param {Function} onNames - Runs once a file's names are listed
 * @return {Object} `upload`, the file being uploaded, the error, and controls
 */
export const useAssetListUpload = (
  form: PackageFormInstance,
  onNames: () => void
) => {
  const [error, setError] = useState<string | null>(null);
  const {
    mutate,
    isPending,
    variables,
    reset: forget,
  } = useMutation<AssetListUploadResponse, Error, File>({
    mutationFn: (file) => {
      const body = new FormData();
      body.append('file', file);
      return api
        .post('packages/asset_list_uploads', { body, timeout: 120_000 })
        .json<AssetListUploadResponse>();
    },
    retry: false,
    // As the name check: offline, fail now rather than wait, paused, on the uploading item.
    networkMode: 'always',
  });

  const upload = useCallback(
    (file: File) => {
      const problem = checkAssetListFile(file);
      setError(problem);
      if (problem) return;
      mutate(file, {
        onSuccess: (response) => {
          if (response.fileErrors.length > 0) {
            setError(response.fileErrors.join(' '));
            return;
          }
          form.setFieldValue('assetList', (prev) => ({
            ...prev,
            file: {
              name: response.fileName ?? file.name,
              assetNames: response.assetNames,
            },
          }));
          onNames();
        },
        onError: () => setError('The file could not be uploaded. Try again.'),
      });
    },
    [form, mutate, onNames]
  );

  const reset = useCallback(() => {
    forget();
    setError(null);
  }, [forget]);

  const dismissError = useCallback(() => setError(null), []);

  return {
    upload,
    uploading: isPending && variables ? variables.name : null,
    error,
    dismissError,
    reset,
  };
};

/**
 * Everything Select Source needs for the From Asset List source: the list's
 * summary, whether it is ready for Next, the panel's props, and `clear` for a
 * tile flip (the text, the file, the answers and any request in flight).
 *
 * @param {PackageFormInstance} form - Wizard form instance
 * @return {Object} `summary`, `readiness`, `panel` and `clear`
 */
export const useAssetListSource = (form: PackageFormInstance) => {
  const input = useStore(form.store, (s) => s.values.assetList);
  const summary = useAssetListSummary(form);
  const { failure } = useAtomValue(assetNameCheckAtom);
  const setCheck = useSetAtom(assetNameCheckAtom);
  const { schedule, cancel } = useAssetNameCheck(form);
  const upload = useAssetListUpload(form, schedule);

  const onTextChange = useCallback(
    (text: string) => {
      form.setFieldValue('assetList', (prev) => ({ ...prev, text }));
      schedule();
    },
    [form, schedule]
  );
  const onRemoveFile = useCallback(
    () => form.setFieldValue('assetList', (prev) => ({ ...prev, file: null })),
    [form]
  );
  const onTryAgain = useCallback(() => schedule(0), [schedule]);

  const { reset: resetUpload } = upload;
  const clear = useCallback(() => {
    cancel();
    resetUpload();
    setCheck(EMPTY_ASSET_NAME_CHECK);
    const list = form.state.values.assetList;
    if (list.text !== '' || list.file !== null) {
      form.setFieldValue('assetList', EMPTY_ASSET_LIST);
    }
  }, [cancel, resetUpload, setCheck, form]);

  const readiness = useMemo(
    () => ({
      matched: summary.matched.length,
      settled: summary.unchecked.length === 0 && upload.uploading === null,
    }),
    [summary, upload.uploading]
  );

  return {
    summary,
    readiness,
    clear,
    panel: {
      input,
      onTextChange,
      onRemoveFile,
      upload,
      summary,
      failure,
      onTryAgain,
    },
  };
};
